
import {
  defineComponent, PropType, Ref, ref, onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InlineMessage from 'primevue/inlinemessage';

import { FieldConfig, ParserFieldObject, Property } from '@/types';
import BaseTextarea from '@/components/fields/BaseTextarea.vue';
import { calculateSmsLength } from '@/components/template-builder/setters';
import { getVariablesWithCollectionContext, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';

export default defineComponent({
  name: 'TextareaVariables',
  components: {
    BaseTextarea,
    InlineMessage,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    disableHandler: {
      type: Function,
      required: false,
      default: null,
    },

    disableMessage: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties ?? []);
    const inputValue: Ref<string> = ref(properties.value[0].value ?? props.configs.options.defaultValue);
    const validationError: Ref<string | null> = ref(null);
    const validationErrorParams: Ref<Record<string, string | number>> = ref({});
    const textareaRef = ref();
    const enrichedVariables = ref(props.configs.options.variablesList || []);

    const validateContent = () => {
      const trimmed = (inputValue.value || '').replace(/<[^>]*>/g, '').trim();

      if (props.configs.options?.required && !trimmed) {
        validationError.value = props.configs.options.requiredErrorKey || null;
        validationErrorParams.value = {};
        return;
      }

      if (props.configs.options?.validator) {
        const validationResult = props.configs.options.validator(inputValue.value);
        validationError.value = validationResult.isValid ? null : validationResult.errorKey;
        validationErrorParams.value = validationResult.errorParams || {};
        return;
      }

      validationError.value = null;
      validationErrorParams.value = {};
    };

    const handleValueChange = () => {
      // Validate if validator is provided
      validateContent();

      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = inputValue.value;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    // Validate on mount to show errors for existing invalid content
    onMounted(async () => {
      validateContent();

      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties to base variables (for use outside loops)
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables from custom events
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    return {
      t,
      inputValue,
      enrichedVariables,
      validationError,
      validationErrorParams,
      textareaRef,
      handleValueChange,
      disableHandler: props.disableHandler,
      disableMessage: props.disableMessage,
    };
  },

  computed: {
    charactersCount(): string {
      if (this.$props.configs.options?.countCharactersFor === 'sms') {
        const { returnMessage } = calculateSmsLength(this.inputValue);
        return returnMessage;
      }

      return '';
    },

    maxLengthTextarea(): number {
      if (this.$props.configs.options?.countCharactersFor === 'sms') {
        const { nbMessages, nbCharactersMax } = calculateSmsLength(this.inputValue);
        return nbMessages === 5 ? nbCharactersMax : -1;
      }

      if (this.$props.configs.options?.maxLength) {
        return this.$props.configs.options.maxLength;
      }

      return -1;
    },
  },
});
